import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { dpwStrategicData } from '../data/dpwStrategicData';
import './DpwStrategic.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

function getAllObjectives(data) {
  const objs = [];
  data.principles.forEach(p => p.objectives.forEach(o => objs.push(o)));
  return objs;
}

function DpwStrategic() {
  const [expandedPrinciples, setExpandedPrinciples] = usePersistentSet('dpw-strategic:expandedPrinciples', () => new Set(dpwStrategicData.principles.map(p => p.id)));
  const [expandedObjectives, setExpandedObjectives] = usePersistentSet('dpw-strategic:expandedObjectives');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('dpw-strategic:overviewExpanded');
  const [keyExpanded, setKeyExpanded] = usePersistentFlag('dpw-strategic:keyExpanded');

  useEffect(() => {
    document.title = 'DPW Strategic Plan';
  }, []);

  const allObjectives = useMemo(() => getAllObjectives(dpwStrategicData), []);

  const togglePrinciple = (id) => {
    setExpandedPrinciples(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleObjective = (id) => {
    setExpandedObjectives(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedPrinciples(new Set(dpwStrategicData.principles.map(p => p.id)));
    setExpandedObjectives(new Set(allObjectives.map(o => o.id)));
    setOverviewExpanded(true);
    setKeyExpanded(true);
  };

  const collapseAll = () => {
    setExpandedPrinciples(new Set());
    setExpandedObjectives(new Set());
    setOverviewExpanded(false);
    setKeyExpanded(false);
  };

  return (
    <div className="dpw-page">
      <a href="#main-content" className="dpw-skip-link">Skip to main content</a>
      <Header
        title="DPW Strategic Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="dpw-content">
        <div className="dpw-intro">
          <p className="dpw-updated">
            Town of Brookline · Department of Public Works · {dpwStrategicData.planYears} 10-Year Strategic Plan
          </p>
        </div>

        <div className="dpw-controls">
          <span className="dpw-count" aria-live="polite">
            {dpwStrategicData.principles.length} guiding principles · {allObjectives.length} objectives
          </span>
          <div className="dpw-expand-controls">
            <button className="dpw-expand-btn" onClick={expandAll}>Expand All</button>
            <button className="dpw-expand-btn" onClick={collapseAll}>Collapse All</button>
          </div>
        </div>

        <div className="dpw-preamble-sections">
          <div className="dpw-preamble">
            <button
              className={`dpw-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`dpw-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="dpw-preamble-title">About This Plan</span>
            </button>
            {overviewExpanded && (
              <div className="dpw-preamble-body">
                {dpwStrategicData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="dpw-preamble">
            <button
              className={`dpw-preamble-header ${keyExpanded ? 'expanded' : ''}`}
              onClick={() => setKeyExpanded(!keyExpanded)}
              aria-expanded={keyExpanded}
            >
              <span className={`dpw-chevron${keyExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="dpw-preamble-title">How This Plan Is Organized</span>
            </button>
            {keyExpanded && (
              <div className="dpw-preamble-body">
                {dpwStrategicData.introduction.itemsNote.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="dpw-principles">
          {dpwStrategicData.principles.map(principle => (
            <div key={principle.id} className="dpw-principle">
              <h2 className="dpw-heading"><button
                className={`dpw-principle-header ${expandedPrinciples.has(principle.id) ? 'expanded' : ''}`}
                onClick={() => togglePrinciple(principle.id)}
                aria-expanded={expandedPrinciples.has(principle.id)}
              >
                <span className={`dpw-chevron dpw-chevron-light${expandedPrinciples.has(principle.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="dpw-principle-header-text">
                  <span className="dpw-principle-eyebrow">Guiding Principle {principle.number}</span>
                  <span className="dpw-principle-name">{principle.name}</span>
                </div>
                <span className="dpw-principle-count" aria-hidden="true">{principle.objectives.length}</span>
              </button></h2>

              {expandedPrinciples.has(principle.id) && (
                <div className="dpw-principle-body">
                  {principle.statement && (
                    <p className="dpw-statement">{principle.statement}</p>
                  )}
                  {principle.objectives.map(o => {
                    const isExpanded = expandedObjectives.has(o.id);
                    return (
                      <div key={o.id} className="dpw-objective">
                        <h3 className="dpw-heading"><button
                          className={`dpw-objective-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleObjective(o.id)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`dpw-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="dpw-objective-id">Objective {o.id}</span>
                          <span className="dpw-objective-title">{o.title}</span>
                        </button></h3>
                        {isExpanded && (
                          <div className="dpw-objective-body">
                            {o.actions && o.actions.length > 0 ? (
                              <>
                                <p className="dpw-actions-label">Action Items</p>
                                <ul className="dpw-actions-list">
                                  {o.actions.map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                              </>
                            ) : (
                              <p className="dpw-no-actions">The plan states this objective without listing specific action items.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="dpw-source">
          <p>
            Source: Town of Brookline, Department of Public Works Strategic Plan {dpwStrategicData.planYears}
            {' '}(final update 2023). This page presents the plan’s five Guiding Principles and their objectives and
            action items; the Department’s vision, mission, core values narrative, and division profiles are in the
            source document. Objective and action lettering follows the source.
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/2283/Public-Works" target="_blank" rel="noopener noreferrer">
              Department of Public Works<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default DpwStrategic;
